import {
  Box,
  Button,
  Inline,
  Modal,
  type ModalProps,
  notifySuccess,
  Stack,
  useModal,
} from '@omnipresentgroup/design-system';
import {
  useCompensationConfiguration,
  useCompensationVariantPaymentMutation,
  useEmployee,
} from '@omnipresentgroup/domains';
import { Form, FormProvider } from '@omnipresentgroup/flexible-forms';
import {
  InputFieldSkeleton,
  RadioFieldSkeleton,
} from '@omnipresentgroup/shared';
import type { CompensationVariantPayment } from '@omnipresentgroup/types';
import { getCountryCode } from 'countries-list';
import { useEffect, useMemo, useState } from 'react';

import { useContractualVariantPaymentSchema } from '../hooks';
import { type ContractualVariantPaymentFormFields, FormAction } from '../types';

type Props = {
  trigger: ModalProps['trigger'];
  variantPayment: CompensationVariantPayment;
};

const FormSkeleton = () => {
  return (
    <Stack gap="16">
      <RadioFieldSkeleton numberOfOptions={2} />
      <InputFieldSkeleton />
      <RadioFieldSkeleton numberOfOptions={1} />
      {Array.from({ length: 3 }, (_, index) => (
        <InputFieldSkeleton key={`input-skeleton-${index}`} />
      ))}
    </Stack>
  );
};

export const EditContractualVariantPaymentModal = ({
  trigger,
  variantPayment,
}: Props) => {
  const { modalProps, isOpen } = useModal({ trigger });

  const [modalOpen, setModalOpen] = useState(false);

  const [formValues, setFormValues] = useState<
    ContractualVariantPaymentFormFields | undefined
  >({
    ...variantPayment,
    amount: {
      amount: Number(variantPayment?.amount.amount),
      currency: variantPayment?.amount.currency,
    },
    reason: variantPayment.reason || undefined,
    additionalNotes: variantPayment.additionalNotes || undefined,
  });

  const formId = useMemo(
    () => `compensation-item-form-${variantPayment.id}`,
    [variantPayment.id],
  );

  const { data: employee, isLoading: isLoadingEmployee } = useEmployee({
    id: variantPayment.employeeId,
    enabled: !!variantPayment.employeeId,
  });

  const employeeCurrency = useMemo(
    () => employee?.employmentDetails?.annualBaseSalary?.currency || '',
    [employee?.employmentDetails?.annualBaseSalary?.currency],
  );

  const employeeCountryCode = useMemo(
    () =>
      ((employee?.country?.name && getCountryCode(employee?.country?.name)) ||
        '') as string,
    [employee?.country?.name],
  );

  const { data: compensationConfig, isLoading: isLoadingConfig } =
    useCompensationConfiguration({
      countryCode: employeeCountryCode,
      source: variantPayment.source,
      enabled: Boolean(employeeCountryCode),
    });

  const itemsConfig = useMemo(
    () => compensationConfig?.variantPaymentsTypes || [],
    [compensationConfig?.variantPaymentsTypes],
  );

  const itemConfig = useMemo(
    () => itemsConfig.find((item) => item.subtype === formValues?.subtype),
    [formValues?.subtype, itemsConfig],
  );

  const { schema: flexibleSchema, uiSchema: flexibleUiSchema } =
    useContractualVariantPaymentSchema({
      itemConfig,
      itemsConfig,
      currency: employeeCurrency,
      action: FormAction.UPDATE,
      formValues,
      variantPayment,
    });

  const {
    mutate: updateVariantPayment,
    isPending: isUpdatingVariantPayment,
    isSuccess,
  } = useCompensationVariantPaymentMutation({
    id: variantPayment.id,
    employeeId: variantPayment.employeeId,
  });

  const isLoading = useMemo(
    () => isLoadingEmployee || isLoadingConfig,
    [isLoadingConfig, isLoadingEmployee],
  );

  // Handle success
  useEffect(() => {
    if (isSuccess) {
      notifySuccess({
        title: 'Variant payment has been updated successfully',
      });
      setModalOpen(false);
    }
  }, [isSuccess]);

  return (
    <Modal
      {...modalProps}
      span={4}
      open={isOpen || modalOpen}
      onOpenChange={setModalOpen}
    >
      <Modal.Header title={'Update compensation item'} />
      <Modal.Body>
        <Box overflow={'hidden'}>
          {isLoading ? (
            <FormSkeleton />
          ) : (
            <FormProvider>
              <Form<ContractualVariantPaymentFormFields>
                id={formId}
                schema={flexibleSchema}
                uiSchema={flexibleUiSchema}
                formData={formValues}
                onChange={({ formData }) => {
                  formData && setFormValues(formData);
                }}
                onSubmit={({ formData }) => {
                  formData &&
                    updateVariantPayment({
                      amount: formData.amount?.amount
                        ? {
                            currency: variantPayment.amount.currency,
                            amount: formData.amount.amount,
                          }
                        : undefined,
                      reason: formData.reason!,
                      additionalNotes: formData.additionalNotes,
                    });
                }}
                liveOmit
                omitExtraData
                omitNonEditableFields
              />
            </FormProvider>
          )}
        </Box>
      </Modal.Body>
      <Modal.Footer>
        <Inline justify="space-between" w="stretch">
          <Button
            variant="secondary"
            onClick={() => setModalOpen(false)}
            disabled={isUpdatingVariantPayment}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form={formId}
            disabled={isLoading}
            loading={isUpdatingVariantPayment}
          >
            Update
          </Button>
        </Inline>
      </Modal.Footer>
    </Modal>
  );
};
